import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { IoTClient } from '@frontend/iot/api';
import { IoT } from '@frontend/iot/types';
import { LabelClient, LabelTemplateClient } from '@frontend/label/api';
import { LabelTemplate } from '@frontend/label/types';
import { useLocale } from '@frontend/lang';
import { notifyEntityCreated } from '@frontend/repository';
import { TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionTriggerName, TransactionUser } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../../redux/store';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { syncWizardTransactionData } from '../../utils/sync-wizard-transaction-data';
import { useScreen } from '../useScreen';

interface ViewProps {
    canPrint: boolean;
    printer: IoT | null;
    transaction: Transaction | null;
    collis: Colli[] | null;
    receivers: TransactionUser[] | null;
    submitDisabled: boolean;
    onSubmit: () => void;
    onPrint: () => void;
    printing: boolean;
    onBack: (screen: ScreenType) => void;
}

const useConfirmTransactionPrintLabels = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep, onCompleteWizard } = useScreen(props.wizard_id);
    const dispatch = useAppDispatch();
    const { refresh } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const { locale } = useLocale();
    const printerLoadedRef = useRef(false);
    const receiversLoadedRef = useRef(false);

    const [printer, setPrinter] = useState<IoT | null>(store?.data.printer || null);
    const [labelTemplate, setLabelTemplate] = useState<LabelTemplate | null>(store?.data.labelTemplate || null);
    const [printing, setPrinting] = useState<boolean>(false);
    const [labelPrinted, setLabelPrinted] = useState<boolean>(store?.data.labelPrinted || false);
    const [receivers, setReceivers] = useState<TransactionUser[] | null>(store?.data.receivers || null);

    const transactionId = store?.data.transaction?.id;

    useEffect(() => {
        if (receiversLoadedRef.current || receivers !== null) return;

        if (transactionId) {
            receiversLoadedRef.current = true;
            TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId }).then((res) =>
                setReceivers(res.results.filter((u) => u.type === 'receiver'))
            );
            return;
        }

        if (store?.data.receiver) {
            receiversLoadedRef.current = true;
            setReceivers([store.data.receiver]);
        }
    }, [receivers, store?.data.receiver, transactionId]);

    useEffect(() => {
        if (printerLoadedRef.current || !store) return;

        const printerId = store.functionalitySettings.find((s) => s.key === 'default_printer_id')?.value;
        const labelTemplateId = store.functionalitySettings.find((s) => s.key === 'label_template_id')?.value;

        if (typeof printerId !== 'string' && typeof labelTemplateId !== 'string') return;

        printerLoadedRef.current = true;

        if (typeof printerId === 'string') {
            IoTClient.fetchIoT(store.wizard.account_id, printerId).then(setPrinter);
        }

        if (typeof labelTemplateId === 'string') {
            LabelTemplateClient.fetchAccountLabelTemplate(store.wizard.account_id, labelTemplateId).then(setLabelTemplate);
        }
    }, [store]);

    const onPrint = async () => {
        if (!printer || !labelTemplate || !store?.data.transaction) return;
        setPrinting(true);

        try {
            const labelResult = await LabelClient.generateTransactionLabel(store.data.transaction.account_id, store.data.transaction.id, {
                language: locale,
                template_id: labelTemplate.id
            });
            const printPromises = labelResult.results.map((label) =>
                LabelClient.printLabel(store.data.transaction!.account_id, label.id, { iot_id: printer.id })
            );
            await Promise.all(printPromises);
            setLabelPrinted(true);
        } finally {
            setPrinting(false);
        }
    };

    const onSubmit = async () => {
        if (!store?.data.transaction || !props.wizard_id) return;
        if (printer && !labelPrinted) return;

        const synced = await syncWizardTransactionData(store);

        updateWizardData({
            transaction: synced.transaction,
            senders: synced.senders,
            receivers: synced.receivers,
            spotId: synced.transaction.spot_id,
            slotId: synced.transaction.slot_id,
            moduleId: synced.location.module_id ?? synced.transaction.module_id
        });

        await TransactionWorkflowClient.triggerTransactionState(synced.transaction.account_id, synced.transaction.id, {
            trigger: TransactionTriggerName.TRANSACTION_PROCESS
        });
        // Draft create already notified; re-notify after PROCESS so Active (non-draft) lists refetch.
        notifyEntityCreated(dispatch, EntityType.TRANSACTION);

        await refresh();

        updateWizardData({ printer, labelPrinted });

        const nextStep = store?.steps.find((step) => step.screen === ScreenType.CONFIRM_TRANSACTION_PRINT_LABELS && step.trigger === ScreenTrigger.SUBMIT_TRUE);
        if (nextStep) navigateToNextStep(nextStep);
        else await onCompleteWizard();
    };

    return {
        canPrint: !!(printer && labelTemplate && store?.data.transaction),
        printer,
        transaction: store?.data.transaction || null,
        collis: store?.data.collis || null,
        receivers,
        submitDisabled: !store || !props.wizard_id || !store.data.transaction || !!(printer && !labelPrinted),
        onSubmit,
        onPrint,
        printing,
        onBack
    };
};

export default useConfirmTransactionPrintLabels;
